import { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetNewReleaseQuery } from '../slices/artworksApiSlice';
import { linkHelpers } from '../utils';

const stateKey = (id) => `nrState:${id}`;

// Gallery routes show the large centered modal; everywhere else shows the
// corner card. Either can collapse to the bulb.
const isGalleryRoute = (path) =>
  path === '/' || path.startsWith('/page/') || path.startsWith('/search/');

// Single orchestrator for the new-release announcement. Renders one of three
// views based on a per-release state persisted in localStorage:
//   'new'       → first appearance (modal on gallery / card elsewhere, after a delay)
//   'open'      → re-expanded by the visitor (no delay)
//   'minimized' → small circular bulb, shown on every page
const NewRelease = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { buildArtworkLink } = linkHelpers;
  const { data: artwork } = useGetNewReleaseQuery();

  const [state, setState] = useState('new');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!artwork?._id) return;
    setState(localStorage.getItem(stateKey(artwork._id)) || 'new');
  }, [artwork]);

  useEffect(() => {
    if (!artwork?._id || state === 'minimized') return;
    if (state === 'new') {
      setVisible(false);
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
    setVisible(true);
  }, [artwork, state]);

  const persist = (next) => {
    if (artwork?._id) localStorage.setItem(stateKey(artwork._id), next);
    setState(next);
  };

  const minimize = () => persist('minimized');
  const expand = () => persist('open');
  const viewPiece = () => {
    persist('minimized');
    navigate(`/artwork/${artwork._id}`);
  };

  const onOwnPage = artwork?._id && pathname === `/artwork/${artwork._id}`;
  if (!artwork?._id || onOwnPage) return null;

  // Minimized → bulb on every page
  if (state === 'minimized') {
    return (
      <button
        className="nr-bulb"
        onClick={expand}
        aria-label={`New release: ${artwork.name}`}
        title={`New release: ${artwork.name}`}
      >
        <img src={buildArtworkLink(artwork.image)} alt={artwork.name} />
      </button>
    );
  }

  // Gallery → large centered modal
  if (isGalleryRoute(pathname)) {
    const meta = [
      artwork.editionNumber ? `Edition of ${artwork.editionNumber}` : null,
      artwork.releaseDate,
    ]
      .filter(Boolean)
      .join(' · ');

    return (
      <Modal show={visible} onHide={minimize} centered>
        <Modal.Header closeButton className="border-0 pb-0" />
        <Modal.Body className="text-center pt-0">
          <img
            src={buildArtworkLink(artwork.image)}
            alt={artwork.name}
            style={{ width: '100%', borderRadius: '6px', marginBottom: '16px' }}
          />
          <div className="nr-eyebrow">New release</div>
          <h4 className="mb-1">{artwork.name}</h4>
          {meta && (
            <div className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
              {meta}
            </div>
          )}
          {artwork.description && (
            <p className="text-muted" style={{ fontSize: '0.9rem' }}>
              {artwork.description}
            </p>
          )}
          <Button onClick={viewPiece} className="w-100 nr-btn">
            View artwork →
          </Button>
          <div className="nr-dismiss" onClick={minimize}>
            Maybe later
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  // Non-gallery → expanded corner card
  if (!visible) return null;
  return (
    <div className="nr-widget" role="dialog" aria-label="New release">
      <button className="nr-widget-close" aria-label="Minimize" onClick={minimize}>
        &times;
      </button>
      <img
        className="nr-widget-img"
        src={buildArtworkLink(artwork.image)}
        alt={artwork.name}
      />
      <div className="nr-widget-body">
        <div className="nr-eyebrow">New release</div>
        <div className="nr-widget-title">{artwork.name}</div>
        <button className="nr-widget-link" onClick={viewPiece}>
          View artwork →
        </button>
      </div>
    </div>
  );
};

export default NewRelease;
